import {Record, type RecordOf} from 'immutable';
import {type Box} from '@packages/utilities/box';
import {
  NodeSelectionType,
  RangeSelectionType,
} from '@packages/shared/types/ContentEditor';

// Note: Selection Records must always be created with all of their required props
export type RangeSelectionRecordShape = {
  anchorNodeId: string;
  focusNodeId: string;
  anchorOffset: number;
  focusOffset: number;
  leftToRight: boolean;
  rangeClientRect?: Box | null;
};

export type NodeSelectionRecordShape = {
  nodeId?: string;
  nodeClientRect?: Box | null;
};

export type SelectionRecordShape =
  | RangeSelectionRecordShape
  | NodeSelectionRecordShape;

export const defaultRangeProps: RangeSelectionType = {
  type: 'Range',
  // When a text range is selected we use anchor/focus system for describing
  // the bounds of that selection against an element subtree
  anchorNodeId: '',
  focusNodeId: '',
  anchorOffset: 0,
  focusOffset: 0,
  leftToRight: true,
  rangeClientRect: null,
} as const;

export const defaultNodeProps: NodeSelectionType = {
  type: 'Node',
  // When a node is selected (like a figure), we don't want to see the caret
  nodeId: '',
  nodeClientRect: null,
} as const;

// Create untyped records with the correct keys
const RangeSelectionRecord_Untyped = Record(
  defaultRangeProps,
  'RangeSelectionRecord'
);
const NodeSelectionRecord_Untyped = Record(
  defaultNodeProps,
  'NodeSelectionRecord'
);

// Use these for the created records
export type RangeSelectionRecordType = RecordOf<RangeSelectionType>;
export type NodeSelectionRecordType = RecordOf<NodeSelectionType>;
export type SelectionRecordType =
  | RangeSelectionRecordType
  | NodeSelectionRecordType;

export const RangeSelectionRecord: (
  arg1: RangeSelectionRecordShape
) => RangeSelectionRecordType = RangeSelectionRecord_Untyped;

export const NodeSelectionRecord: (
  arg1: NodeSelectionRecordShape
) => NodeSelectionRecordType = NodeSelectionRecord_Untyped;
